import React from "react";

export function decodeHTMLEntities(str: string): string {
  if (!str) return "";
  return str
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'");
}

export function stripHTML(htmlString: string = ""): string {
  if (!htmlString) return "";

  const clean = htmlString.replace(/<\/?[^>]+(>|$)/g, "");

  return decodeHTMLEntities(clean);
}

export function stripHTMLKeepBreak(htmlString: string = ""): string {
  if (!htmlString) return "";

  let clean = htmlString;

  clean = clean.replace(/<br\s*\/?>|<\/br>/gi, "\n");
  clean = clean.replace(/<\/?[^>]+(>|$)/g, "");

  return decodeHTMLEntities(clean).trim();
}

export function renderTextWithBreaks(
  text?: string,
  lineGapClass: string = "mt-1 sm:mt-1.5",
): React.ReactNode {
  if (!text) return null;
  if (typeof text !== "string") return text as React.ReactNode;

  const normalized = text.replace(/\\n/g, "\n");

  const parts = normalized
    .split(/<br\s*\/?>|<\/br>|\n/gi)
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length <= 1) return text;

  return React.createElement(
    "span",
    { className: "inline-block w-full", style: { lineHeight: "1.15" } },
    parts.map((part, idx) =>
      React.createElement(
        "span",
        {
          key: idx,
          className: idx === 0 ? "block" : `block ${lineGapClass}`,
          style: { lineHeight: "1.15" },
        },
        part,
      ),
    ),
  );
}

export function stripHTMLKeepBreakWithFont(
  htmlString: string = "",
  font: string = "Montserrat",
): string {
  if (!htmlString) return "";

  let clean = htmlString;

  clean = clean.replace(/<br\s*\/?>/gi, "<br/>");
  clean = clean.replace(/<\/?[^>]+(>|$)/g, "");

  return `<div style="font-family: '${font}', sans-serif; white-space: pre-line;">${clean}</div>`;
}

export function sanitizeHTMLKeepStyleWithFont(
  htmlString: string = "",
  font: string = "Montserrat",
): string {
  if (!htmlString) return "";

  let clean = htmlString;

  clean = clean.replace(/font-family:[^;"]+;?/gi, "");
  clean = clean.replace(/font-size:[^;"]+;?/gi, "");
  clean = clean.replace(
    /style="([^"]*)"/gi,
    (styleContent: any) =>
      `style="font-family: '${font}', sans-serif; ${styleContent}"`,
  );

  clean = clean.replace(
    /<(p|span|div|h1|h2|h3|h4|h5|li|td|th)(?![^>]*style=)/gi,
    `<$1 style="font-family: '${font}', sans-serif;"`,
  );

  return clean;
}

export interface ParsedProductHTML {
  links: any[] | null;
  descriptionHTML: string;
}

export function parseStructuredListFromHTML(
  rawHTML = "",
  {
    itemDelimiter = "***",
    fieldDelimiter = "::",
    fields = [],
    strip = stripHTML,
  }: {
    itemDelimiter?: string;
    fieldDelimiter?: string;
    fields?: string[];
    strip?: (htmlString?: string) => string;
  } = {},
) {
  if (!rawHTML) return [];

  const clean = strip(rawHTML);

  return clean
    .split(itemDelimiter)
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => {
      const parts = item.split(fieldDelimiter).map((p) => p.trim());

      // If schema provided → map to object
      if (fields.length) {
        const obj: Record<string, string | null> = {};

        fields.forEach((key, index) => {
          obj[key] = parts[index] !== undefined ? parts[index] : null;
        });

        return obj;
      }

      // Fallback → return raw array
      return parts;
    });
}
// utils/parseHtmlTextToJson.ts

export function parseHtmlTextToJson<T = any>(htmlText?: string): T[] {
  if (!htmlText || typeof htmlText !== "string") return [];

  try {
    // 1. Decode HTML entities and strip tags
    let text: string = decodeHTMLEntities(htmlText)
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
      .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
      .replace(/<[^>]+>/g, "")
      .trim();

    if (!text) return [];

    text = text.replace(/\s+/g, " ");

    // 2. Direct JSON.parse attempt
    try {
      let jsonText = text;
      if (!jsonText.startsWith("[") && !jsonText.startsWith("{")) {
        jsonText = `[${jsonText}]`;
      }
      const parsed = JSON.parse(jsonText);
      if (Array.isArray(parsed)) return parsed as T[];
      if (parsed && typeof parsed === "object") return [parsed as T];
    } catch {
      // Continue to next attempt
    }

    // 3. Normalized JSON.parse attempt (quote unquoted keys, remove trailing commas)
    try {
      let jsonText = text;
      if (!jsonText.startsWith("[") && !jsonText.startsWith("{")) {
        jsonText = `[${jsonText}]`;
      }
      jsonText = jsonText
        .replace(/([{,]\s*)([a-zA-Z0-9_]+)\s*:/g, '$1"$2":')
        .replace(/,\s*}/g, "}")
        .replace(/,\s*]/g, "]");
      const parsed = JSON.parse(jsonText);
      if (Array.isArray(parsed)) return parsed as T[];
      if (parsed && typeof parsed === "object") return [parsed as T];
    } catch {
      // Continue to next attempt
    }

    // 4. JS object literal evaluation attempt for non-strict JSON (single quotes, etc.)
    try {
      let evalText = text;
      if (!evalText.startsWith("[") && !evalText.startsWith("{")) {
        evalText = `[${evalText}]`;
      }
      const parsed = new Function(`return (${evalText});`)();
      if (Array.isArray(parsed)) return parsed as T[];
      if (parsed && typeof parsed === "object") return [parsed as T];
    } catch {
      // Fallback failed
    }

    return [];
  } catch {
    return [];
  }
}
